import {
  AuthFill,
  Button,
  ClosestDealers,
  Comments,
  Date,
  Email,
  FindDealer,
  FirstName,
  LastName,
  MarketingConsent,
  Models,
  Tel,
  Title,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import {
  closeOverlay,
  getModels,
  getPageData,
  removeDuplicate,
} from '@utils/helpers'
import { formEvent, updateDatalayer } from '@utils/helpers/tracking'
import { VideoConsultationSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from 'upper-case-first'

const VideoConsultation = ({
  models,
  content,
  tracking,
  brand,
  dealer,
  model,
  query,
}) => {
  const campaign = useCampaign()
  const [dealers, setDealers] = useState([])
  const [isDealer, setIsDealer] = useState(false)
  const [searchTerm, setSerchTerm] = useState('')

  const availability = (date) => date.getDay() !== 0 // exclude sunday

  const [submitResponse, setSubmitResponse] = useState('')

  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    tel: '',
    model: '',
    date: '',
    time: '',
    consent: {
      user: false,
    },
    response_code: '',
    campaign_response_code: '',
    campaign_code: '',
    crm_response_source: '',
    is_dealer: query?.source === 'dealer' || query?.source === 'centre',
    dealer: '',
    dealer_name: '',
    notes: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(VideoConsultationSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const selectedDealer = methods.watch('dealer')

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.utm) {
      methods.setValue('crm_response_source', `Web ${campaign.utm}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (dealer) {
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      methods.setValue('dealer_name', dealer.name)
      methods.setValue('is_dealer', true)
      setIsDealer(true)
      updateDatalayer('dealer', {
        uuid: dealer.uuid,
        name: dealer.name,
        address: {
          city: dealer.addresses[0].city,
          zip: dealer.addresses[0].zip,
        },
      })
    }
  }, [dealer])

  useEffect(() => {
    if (model) {
      methods.setValue('model', model.d4t4.id)
      methods.trigger('model')
    }
  }, [model])

  useEffect(() => {
    const dealerName = dealers.filter((i) => {
      return i.value === selectedDealer
    })

    if (dealerName.length >= 1) {
      methods.setValue('dealer_name', dealerName[0]?.label)
      updateDatalayer('dealer', {
        uuid: dealerName[0]?.uuid,
        name: dealerName[0]?.name,
        address: {
          city: dealerName[0]?.address.city,
          zip: dealerName[0]?.address.zip,
        },
      })
    }
  }, [selectedDealer])

  const Dealer = () => {
    return (
      <>
        {!isDealer && (
          <FindDealer
            isError={'dealer' in methods.formState.errors && !dealers.length}
            label={`${brand === 'lexus' ? 'Choose' : 'Find'} your preferred ${
              brand === 'lexus' ? 'centre' : 'dealer'
            }`}
            infoText="Enter your postcode/location"
            value={searchTerm}
            search={(dealersSearch, term) => {
              setSerchTerm(term)
              const dealersOptions = dealersSearch.map((dealerSearch) => ({
                label: dealerSearch?.name,
                value: dealerSearch?.localDealerID,
                name: dealerSearch?.name,
                uuid: dealerSearch?.uuid,
                address: dealerSearch?.address,
              }))
              if (dealersOptions.length) {
                const dealerList = removeDuplicate(dealersOptions, 'value')
                setDealers(dealerList)
                methods.setValue('dealer', dealerList[0].value, {
                  shouldValidate: true,
                })
              }
            }}
          />
        )}

        {dealers.length > 0 && !isDealer && (
          <ClosestDealers
            label={`${
              brand === 'lexus'
                ? 'Please select a Lexus Centre'
                : 'Nominated Dealers'
            }`}
            helper={`${
              brand === 'lexus'
                ? 'List displays the closest centre to your postcode.'
                : 'List displays the closest dealer to your postcode.'
            }`}
            dealers={dealers}
          />
        )}
      </>
    )
  }

  const onSubmit = async (data) => {
    const formData = data
    formData.response_code = brand === 'lexus' ? 'RC116737' : 'RC120152'

    if (formData.is_dealer) {
      formData.response_code = brand === 'lexus' ? 'RC116737' : 'RC120153'
    }

    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/video-consultation`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formData,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: '',
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content?.confirmation?.copy || '',
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: '',
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav title={content.title} />
      <FormWrapper>
        <FormStep subtitle={content.details.subtitle} active={true}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Models models={models} />
              <div className="h-8"></div>
              <Dealer />
              <Date
                availability={availability}
                showTime={true}
                helper="Let us know if you have a preferred date or time and we'll do our best to arrange your booking for this time."
              />
              <div className="h-8"></div>
              <Title optional />
              <FirstName />
              <LastName />
              <Email />
              <Tel
                optional
                helper="We will use your phone number to discuss your consultation over the phone."
              />
              <Comments
                optional
                label="Additional Comments"
                helper="Let us know if you have any specific requirements."
                characterLimit={brand === 'lexus' ? 140 : 255}
              />
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'video-consultation'
  const content = await import(`../content/${brand}/forms/${form}.md`).catch(
    () => null,
  )
  const settings = await import(`../content/${brand}/settings/global.md`).catch(
    () => null,
  )

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }
  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }
  return {
    props: {
      models: getModels(brand, 'video-consulation'),
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'video-consultation',
        form: 'lead-video-consultation',
        variant: '',
        initialStep: 'contact-details',
      },
      description: `${upperCaseFirst(brand)} copy missing`,
      ...(await getPageData(context, form)),
    },
  }
}

VideoConsultation.whyDidYouRender = true

export default VideoConsultation
